import React, { useState, useEffect, useContext, useRef } from "react";
import cx from "classnames";
import styles from "../scss/AFSignature.module.scss";
import { useHistory, useParams } from "react-router-dom";
import AFSecure from "../components/AFSecure";
import Input from "../../common/components/Forms/Input/Input";
import { AppFormControllerContext } from "../providers/AppFormProvider";
import { IViewContext, ViewContext } from "../providers/ViewStateProvider";
import { setCurrentPageProgress, sign } from "../api/ApiAppForm";
import { Form, Formik } from "formik";
import AFLayout from "../components/AFLayout";
import { format } from "date-fns";
import { PageType } from "../enums/PageType";
import { useAFForm } from "../hooks/useAFForm";
import { redirectTo404Page } from "../shared/AppFormHelper";
import AFNavigation from "../components/AFNavigation";
import { IApiSignInfo } from "../interfaces/IApiAppFormData";
import { IPageInfo } from "../interfaces/IAppFormData";
import * as Sentry from "@sentry/react";

interface IAFSignatureProps {
    applicationId: string;
    applicant: string;
}

const AFSignaturePage: React.FC = () => {
    const history = useHistory();
    const params = useParams<IAFSignatureProps>();
    const applicationId = params.applicationId;
    const applicantParams = params.applicant;
    const { setError } = useContext<IViewContext>(ViewContext);


    const [isReady, setIsReady] = useState(false);
    const [firstname, setFirstname] = useState("");
    const [isChild, setIsChild] = useState(false);
    const [signature, setSignature] = useState("");

    const currentDate = format(new Date(), "d MMMM yyyy");

    const { actions, selectors } = useContext(AppFormControllerContext);

    // Ensure we have the applicant info available.
    const applicantInfo = selectors.getCurrentApplicant(params.applicant);

    const {
        getCurrentPage,
        checkValidApplication
    } = useAFForm({ pageType: PageType.Signature, currentClientNumber: applicantInfo?.clientNumber ?? 1 });

    const handleNextPage = (): void => {
        setSignature("");
        const applicantList = selectors.getApplicantList();
        const currentApplicant = applicantList.find((x) => x.params == applicantParams);
        const nextApplicant = applicantList.find((x) => x.clientNumber === currentApplicant.clientNumber + 1);
        if (!nextApplicant) {
            const pageInfo: IPageInfo = {
                pageType: PageType.Submit,
                currentClientNumber: 1
            }
            setCurrentPageProgress(selectors.getApplicationId(), selectors.getQuoteGroupId(), pageInfo
            )
            history.push(`/app-form/submit/${applicationId}/ready`);
            return;
        }
        const pageInfo: IPageInfo = {
            pageType: PageType.Signature,
            currentClientNumber: nextApplicant.clientNumber
        }
        setCurrentPageProgress(selectors.getApplicationId(), selectors.getQuoteGroupId(), pageInfo
        )
        history.push(
            `/app-form/submit/${applicationId}/applicant${nextApplicant?.clientNumber}/signature`
        );
    };

    const handleSubmit = (): void => {
        const applicationId = selectors.getApplicationId();
        const applicants = selectors.getApplicantList();
        const currentApplicant = selectors.getApplicantList()?.find((i) => i.params === applicantParams);
        const newApplicants = applicants.map((item) => ({
            ...item,
            signature: item.firstName === firstname ? signature : item.signature
        }));

        const signData: IApiSignInfo = {
            clientNumber: currentApplicant.clientNumber,
            signature
        };
        sign(applicationId, selectors.getQuoteGroupId(), currentApplicant.applicantId, signData)
            .then(() => {
                actions.setApplicantList(newApplicants);
                handleNextPage();
            })
            .catch(() => {
                setError("There was a problem recording your signature.");
                if (typeof Sentry !== 'undefined') {
                    Sentry.captureMessage(`Error occured for ApplicationId ${applicationId} on the Signature page. There was a problem recording signature.`, "error");
                }
            });
    };

    const handleBack = (e): void => {
        e.preventDefault();
        setIsReady(false);
        if (applicantParams === "applicant1") {
            history.push(`/app-form/submit/${applicationId}/consent/cleanskin`);
        } else {
            const applicantList = selectors.getApplicantList();
            const currentApplicant = applicantList.find((x) => x.params == applicantParams);
            const previousApplicant = applicantList.find((x) => x.clientNumber === currentApplicant.clientNumber - 1);
            history.push(`/app-form/submit/${applicationId}/applicant${previousApplicant.clientNumber}/signature`);
        }
    };

    const elementRef = useRef(null);
    const handleElementRef = (el) => {
        elementRef.current = el;
    };

    // This is only called from within the effect invoked when loading a different applicant (or initial load)
    const checkApplicant = (applicantParams) => {
        const applicant = selectors.getCurrentApplicant(applicantParams);
        if (!applicant || !applicant?.personId) redirectTo404Page();
    };

    useEffect(() => {
        if (!applicantParams) return;
        checkApplicant(applicantParams);
        checkValidApplication(applicationId, selectors);
        if (elementRef.current) {
            elementRef.current.focus();
        }
        const info = selectors.getCurrentApplicant(applicantParams);
        setFirstname(info?.firstName);
        setIsChild(info?.isChild);
        const applicants = selectors.getApplicantList();
        const sign =
            applicants.find((i) => i.firstName === info?.firstName)?.signature || "";
        setSignature(sign);
    }, [applicantParams]);

    return (
        <AFLayout currentPage={getCurrentPage()}>
            <div className={cx(styles["af-signature-page"], styles["layout"])}>
                <div
                    className={cx(
                        styles["af-signature-container"],
                        styles["layout-flex-column--centered"]
                    )}>
                    <div className={cx(styles["signature-title-label"])}>Signature</div>

                    <Formik initialValues={{}} onSubmit={handleSubmit}>
                        {() => (
                            <Form autoComplete="off" className={styles["af-signature"]}>
                                <p className={styles["af-signature-info"]}>
                                    Your insurer has a Declaration Page, which each policy
                                    owner will need to sign (by drawing their signature or
                                    typing their name below) before your application can
                                    be assessed.
                                </p>

                                <div className={styles["af-bank-signature"]}>
                                    <label
                                        className={styles["af-bank-signature--label"]}
                                        htmlFor={`Signature (${firstname})`}>
                                        Signature ({firstname}{isChild && `'s parent/legal guardian`})
                                    </label>
                                    <Input
                                        id={`Signature (${firstname})`}
                                        name={`Signature (${firstname})`}
                                        type="text"
                                        value={signature}
                                        onChange={(e) => {
                                            setSignature(e.target.value);
                                        }}
                                        textRef={handleElementRef}
                                    />
                                    <div className={styles["af-bank-signature--span"]}>
                                        To sign please type your full name
                                    </div>
                                </div>

                                <div className={styles["af-bank-signature"]}>
                                    <div className={styles["af-bank-signature--label"]}>
                                        Date
                                    </div>
                                    <div
                                        className={
                                            styles["af-bank-signature--mini-label"]
                                        }>
                                        {currentDate}

                                    </div>
                                </div>
                                <AFNavigation
                                    handleBack={handleBack}
                                    skipIsVisible={false}
                                    forwardIsVisible={true}
                                    forwardIsDisabled={!signature}
                                    forwardType="submit"
                                    forwardText={"Next"}
                                />
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </AFLayout>
    );
};

export default AFSignaturePage;
